import { OrderType, OrderTypeLabel } from "../../directus/collections";
import { formatSecondsToDDHHMMSS } from "../../libs/utils";
import { MatchFilter } from "../../libs/statsFilters";

export interface FilterChipData {
  key: string;
  label: string;
  value: any;
  category: "orderType" | "duration" | "dateRange" | "emails" | "challenge" | "state" | "progress";
  onRemove?: (filters: MatchFilter) => void;
}

/**
 * Formatta una data in formato leggibile
 */
const formatDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleDateString();
};

/**
 * Converte i filtri orderType in chip data
 */
const createOrderTypeChips = (orderTypes: OrderType[]): FilterChipData[] => {
  return orderTypes.map((orderType, index) => ({
    key: `orderType-${index}`,
    label: `Tipo ordine: ${OrderTypeLabel[orderType]}`,
    value: orderType,
    category: "orderType" as const,
    onRemove: (filters) => {
      filters.orderType = filters.orderType?.filter((ot) => ot !== orderType);
    },
  }));
};

/**
 * Converte i filtri di durata in chip data
 */
const createDurationChips = (duration: {
  min?: number | null;
  max?: number | null;
}): FilterChipData[] => {
  const chips: FilterChipData[] = [];

  if (duration.min != null) {
    chips.push({
      key: "duration-min",
      label: `Durata minima partita: ${formatSecondsToDDHHMMSS(
        (duration.min ?? 0) / 1000
      )}`,
      value: duration.min,
      category: "duration",
      onRemove: (filters) => {
        filters.duration = { ...filters.duration, min: null };
      },
    });
  }

  if (duration.max != null) {
    chips.push({
      key: "duration-max",
      label: `Durata massima partita: ${formatSecondsToDDHHMMSS(
        (duration.max ?? 0) / 1000
      )}`,
      value: duration.max,
      category: "duration",
      onRemove: (filters) => {
        filters.duration = { ...filters.duration, max: null };
      },
    });
  }

  return chips;
};

/**
 * Converte i filtri di date range in chip data
 */
const createDateRangeChips = (dateRange: {
  startDate?: number | null;
  endDate?: number | null;
}): FilterChipData[] => {
  const chips: FilterChipData[] = [];

  if (dateRange.startDate != null) {
    chips.push({
      key: "dateRange-start",
      label: `Data da: ${formatDate(dateRange.startDate)}`,
      value: dateRange.startDate,
      category: "dateRange",
      onRemove: (filters) => {
        filters.dateRange = { ...filters.dateRange, startDate: null };
      },
    });
  }

  if (dateRange.endDate != null) {
    chips.push({
      key: "dateRange-end",
      label: `Data a: ${formatDate(dateRange.endDate)}`,
      value: dateRange.endDate,
      category: "dateRange",
      onRemove: (filters) => {
        filters.dateRange = { ...filters.dateRange, endDate: null };
      },
    });
  }

  return chips;
};

/**
 * Converte i filtri email in chip data
 */
const createEmailChips = (emails: { exclude?: string[] }): FilterChipData[] => {
  if (!emails.exclude || emails.exclude.length === 0) {
    return [];
  }

  return emails.exclude.map((email, index) => ({
    key: `email-exclude-${index}`,
    label: `Email escluse: ${email}`,
    value: email,
    category: "emails" as const,
    onRemove: (filters) => {
      filters.emails = { ...filters.emails, exclude: [] };
    },
  }));
};

/**
 * Converte i filtri challenge in chip data
 */
const createChallengeChips = (challenge: {
  duration?: { min?: number | null; max?: number | null };
}): FilterChipData[] => {
  if (!challenge.duration) {
    return [];
  }

  const chips: FilterChipData[] = [];

  if (challenge.duration.min != null) {
    chips.push({
      key: "challenge-duration-min",
      label: `Durata minima enigma: ${formatSecondsToDDHHMMSS(
        challenge.duration.min / 1000
      )}`,
      value: challenge.duration.min,
      category: "challenge",
      onRemove: (filters) => {
        filters.challenge = {
          ...filters.challenge,
          duration: { ...filters.challenge?.duration, min: null },
        };
      },
    });
  }

  if (challenge.duration.max != null) {
    chips.push({
      key: "challenge-duration-max",
      label: `Durata massima enigma: ${formatSecondsToDDHHMMSS(
        challenge.duration.max / 1000
      )}`,
      value: challenge.duration.max,
      category: "challenge",
      onRemove: (filters) => {
        filters.challenge = {
          ...filters.challenge,
          duration: { ...filters.challenge?.duration, max: null },
        };
      },
    });
  }

  return chips;
};

/**
 * Converte i filtri di stato in chip data
 */
const createStateChips = (state: "created" | "started" | "fulfilled" | "completed"): FilterChipData[] => {
  const stateLabels = {
    created: "Creato",
    started: "Iniziato",
    fulfilled: "Soddisfatto", 
    completed: "Completato"
  };

  return [{
    key: "state",
    label: `Stato: ${stateLabels[state]}`,
    value: state,
    category: "state" as const,
    onRemove: (filters) => {
      filters.state = undefined;
    },
  }];
};

/**
 * Converte i filtri di progresso in chip data
 */
const createProgressChips = (progress: { minChallenges?: number }): FilterChipData[] => {
  if (progress.minChallenges == null || progress.minChallenges === 0) {
    return [];
  }

  return [{
    key: "progress-minChallenges",
    label: `Sfide minime completate: ${progress.minChallenges}`,
    value: progress.minChallenges,
    category: "progress" as const,
    onRemove: (filters) => {
      filters.progress = { ...filters.progress, minChallenges: undefined };
    },
  }];
};

/**
 * Converte un MatchFilter in un array di FilterChipData
 */
export const convertFiltersToChips = (
  filters: MatchFilter
): FilterChipData[] => {
  const chips: FilterChipData[] = [];

  if (filters.orderType && filters.orderType.length > 0) {
    chips.push(...createOrderTypeChips(filters.orderType));
  }

  if (filters.state) {
    chips.push(...createStateChips(filters.state));
  }

  if (filters.duration) {
    chips.push(...createDurationChips(filters.duration));
  }

  if (filters.dateRange) {
    chips.push(...createDateRangeChips(filters.dateRange));
  }

  if (filters.emails) {
    chips.push(...createEmailChips(filters.emails));
  }

  if (filters.progress) {
    chips.push(...createProgressChips(filters.progress));
  }

  if (filters.challenge) {
    chips.push(...createChallengeChips(filters.challenge));
  }

  return chips;
};
